import { TILE_COLORS, TileColor } from '../../TileColor';
import { BOARD_SETTINGS } from '../../../scene/GameSceneConstants';
import type { TileStackModel } from './TileStackModel';
import { getRandomElement } from '../../../../utils/array-utils';
import type { BoardModel } from './BoardModel';
import type { BlockerModel, BoardScheduler } from '../../types';
import { BlockerType } from '../../types';
import { PlankBlockerModel } from '../blockers/PlankBlockerModel';
import type { TasksRunner } from '../../../../libs/TasksRunner';

interface MatchOrigin {
  color: TileColor;
  stack: TileStackModel;
}

export interface PathStep {
  readonly parentStack: TileStackModel;
  readonly childStack: TileStackModel;
}

interface MatchSequencerSettings {
  readonly board: BoardModel;
  readonly taskExecutor: TasksRunner;
  readonly scheduler: BoardScheduler;
  readonly onPathResolved: (stack: TileStackModel) => void;
}

export const adjacentMatchBlockerTypes = [
  BlockerType.Plank,
  BlockerType.Tape,
  BlockerType.Knot,
  BlockerType.BirdHouse,
  BlockerType.Rocket,
  BlockerType.MeasureTape,
];

export class MatchSequencerMVC {
  private static readonly minStacksForOriginSwap = 3;

  private readonly settings: MatchSequencerSettings;

  #activeMatches = 0;

  constructor(settings: MatchSequencerSettings) {
    this.settings = settings;
  }

  /**
   * Check matches connected to a TileStackModel and starts the removal process if a path is found.
   * @param stack
   * @param startDelay
   */
  public checkMatches(stack: TileStackModel, startDelay = BOARD_SETTINGS.timing.defaultStartDelay): void {
    const color = stack.getTopmostColor();
    if (color === TileColor.None) {
      return;
    }

    if (!stack.hasMatchingConnections(color, true)) {
      return;
    }

    const origin: MatchOrigin = this.getPathOrigin(stack);

    // Start the generator - it will be executed step by step
    this.#activeMatches += 1;
    const generator = this.removePath(origin, startDelay);

    this.settings.taskExecutor.addTask(generator);
  }

  /**
   * Get the removal path origin. The originStack gets swapped in certain cases.
   * @param originStack
   */
  private getPathOrigin(originStack: TileStackModel): MatchOrigin {
    const origin = { stack: originStack, color: originStack.getTopmostColor() };
    const matchingConnections = origin.stack.getMatchingConnections(origin.color, (stack) => stack.isBlocked());
    if (matchingConnections.length === 0) return origin;

    // get total tile value of match.
    const matchValue = this.getMatchTotalValue(originStack);

    // check override behaviour for adjacent blockers.
    if (matchValue >= BOARD_SETTINGS.matchLength) {
      const blockerOverride = this.getBlockerOriginOverride(originStack, matchingConnections);
      if (blockerOverride) {
        origin.stack = blockerOverride;
        return origin;
      }

      const underTileBlockerOverride = this.getUnderTileBlockerOriginOverride(originStack, matchingConnections);
      if (underTileBlockerOverride) {
        origin.stack = underTileBlockerOverride;
        return origin;
      }
    }

    // 3 spaces or fewer cases
    if (this.shouldStartAtOrigin(originStack)) return origin;

    const originNextColor = origin.stack.getNextCoveredColor();
    const originNextColorMatches =
      originNextColor !== TileColor.None && origin.stack.hasMatchingConnections(originNextColor, true);

    // Swap the origin if the originStack has match possible for the next color in the stack.
    if (originNextColorMatches) {
      origin.stack = getRandomElement(matchingConnections);
      return origin;
    }

    // Swap the origin stack if it has more then 1 color and is next to a matching stack of a single color.
    if (originNextColor !== TileColor.None) {
      for (let i = 0; i < matchingConnections.length; i += 1) {
        const nextStack = matchingConnections[i];
        const nextStackIsSingleColor = nextStack.getNextCoveredColor() === TileColor.None;
        if (nextStackIsSingleColor) {
          origin.stack = nextStack;
          return origin;
        }
      }
    }
    return origin;
  }

  private shouldStartAtOrigin(originStack: TileStackModel): boolean {
    const emptyStackCount = this.settings.board.getEmptyStackCount();

    if (emptyStackCount > MatchSequencerMVC.minStacksForOriginSwap) return false;

    const nextColorMatchingTiles = this.getMatchTotalValue(originStack, true);
    const nextColorHasConnections = originStack.hasMatchingConnections(originStack.getNextCoveredColor(), true);
    const nextColorHasMatch = nextColorMatchingTiles >= BOARD_SETTINGS.matchLength;

    // when no space it should move for a match or empty space
    if (emptyStackCount === 0) {
      return !(nextColorHasMatch || this.canRedistributeAllColors(originStack));
    }

    // use other stacks if tiles can be moved
    return !nextColorHasConnections;
  }

  private getBlockerOriginOverride(
    originStack: TileStackModel,
    matchingConnections: TileStackModel[],
  ): TileStackModel | undefined {
    const color = originStack.getTopmostColor();
    if (color === TileColor.None) return undefined;

    const connectedBlockers = [originStack, ...matchingConnections].map((s) => ({
      stack: s,
      blockers: this.getStackConnectedBlockers(s),
    }));

    const neighbourOfMatchingColorPlanks = connectedBlockers.find(({ blockers }) =>
      blockers.some((b) => b instanceof PlankBlockerModel && b.getColor() === color),
    )?.stack;

    if (neighbourOfMatchingColorPlanks) {
      return neighbourOfMatchingColorPlanks;
    }

    return connectedBlockers.find(({ blockers }) =>
      blockers.some((blocker) => {
        if (blocker instanceof PlankBlockerModel && blocker.getColor() !== TileColor.None) return false;
        return adjacentMatchBlockerTypes.includes(blocker.getType());
      }),
    )?.stack;
  }

  private getUnderTileBlockerOriginOverride(
    originStack: TileStackModel,
    matchingConnections: TileStackModel[],
  ): TileStackModel | null {
    if (originStack.underTileBlocker) return originStack;
    return matchingConnections.find((stack) => stack.underTileBlocker) ?? null;
  }

  private getStackConnectedBlockers(stack: TileStackModel): BlockerModel[] {
    return stack
      .getConnectedStacks()
      .filter((s) => s.blocker)
      .map((s) => s.blocker) as BlockerModel[];
  }

  private canRedistributeAllColors(originStack: TileStackModel): boolean {
    const colorsOfNeighbours = new Set(originStack.getConnectedStacks().map((stack) => stack.getTopmostColor()));
    return originStack.getTiles().every((tile) => colorsOfNeighbours.has(tile.color));
  }

  /**
   * Starts the path removal sequence and locks the path and generated secondary steps.
   * @param origin
   * @param startDelay
   */
  private *removePath(origin: MatchOrigin, startDelay: number): Generator<void, void> {
    const { scheduler, onPathResolved } = this.settings;

    origin.stack.locked = true;

    yield* scheduler.waitGenerator(null, startDelay);

    const steps = this.generatePathSteps(origin.stack, origin.color);
    if (steps.length > 0) yield* this.runPathSteps(steps);

    origin.stack.locked = false;
    this.checkMatches(origin.stack, BOARD_SETTINGS.timing.secondaryMatchStartDelay);

    this.#activeMatches -= 1;
    onPathResolved(origin.stack);
  }

  /**
   * Run path removal steps. These feed into the primary path.
   * @param steps
   */
  private *runPathSteps(steps: PathStep[]): Generator<void, void> {
    const { board } = this.settings;

    for (let j = steps.length - 1; j >= 0; j -= 1) {
      const { parentStack, childStack } = steps[j];

      // Check if any new stack can be pulled into the sequence.
      const newSteps = this.generatePathSteps(childStack, childStack.getTopmostColor(), []);
      if (newSteps.length > 0) yield* this.runPathSteps(newSteps);

      yield* board.transferTiles(
        childStack,
        parentStack,
        childStack.getMatchingColorCount(childStack.getTopmostColor()),
      );
      this.checkMatches(childStack, BOARD_SETTINGS.timing.secondaryMatchStartDelay);
    }
  }

  /**
   * Recursively generates PathStep(s) that feed in to the main path.
   * @param originStack
   * @param color
   * @param output
   */
  private generatePathSteps(originStack: TileStackModel, color: TileColor, output: PathStep[] = []): PathStep[] {
    const secondaryStacks = originStack.getMatchingConnections(color, (stack) => stack.isBlocked());
    if (secondaryStacks.length === 0) {
      return output;
    }

    for (let i = 0; i < secondaryStacks.length; i += 1) {
      const secondaryStack = secondaryStacks[i];
      if (secondaryStack.isBlocked()) {
        continue;
      }

      secondaryStack.locked = true;
      output.push({
        parentStack: originStack,
        childStack: secondaryStack,
      });
      this.generatePathSteps(secondaryStack, color, output);
    }
    return output;
  }

  /**
   * Searches all connected TileStacks with a matching color and returns the total matching tile count in all stacks.
   * @param originStack
   * @param useNextColor option to use the next color, rather than the topmost color.
   */
  private getMatchTotalValue(originStack: TileStackModel, useNextColor = false): number {
    const checkedSet = new Set<TileStackModel>();
    const addToCheckedSet = (s: TileStackModel): void => {
      checkedSet.add(s);
    };

    const color = useNextColor ? originStack.getNextCoveredColor() : originStack.getTopmostColor();
    if (color === TileColor.None) return 0;

    let matchValue = useNextColor ? originStack.getNextColorCount() : originStack.getMatchingColorCount(color);

    const stacksToCheck: TileStackModel[] = [originStack];
    addToCheckedSet(originStack);

    while (stacksToCheck.length > 0) {
      const stack = stacksToCheck.shift();
      if (!stack) break;

      const connectedStacks = stack.getMatchingConnections(color, (s) => s.isBlocked() || checkedSet.has(s));
      stacksToCheck.push(...connectedStacks);
      connectedStacks.forEach(addToCheckedSet);

      // originStack color count already added / differs based on useNextColor option.
      if (stack !== originStack) matchValue += stack.getMatchingColorCount(color);
    }
    return matchValue;
  }

  get activeMatches(): number {
    return this.#activeMatches;
  }

  public getMatchValuesForStack(originalStack: TileStackModel): Record<TileColor, number> {
    const result: Partial<Record<TileColor, number>> = {};

    const checkedSet = new Set<TileStackModel>();

    TILE_COLORS.forEach((color): void => {
      const stacksToCheck: TileStackModel[] = [originalStack];

      for (let i = 0; i < stacksToCheck.length; i += 1) {
        const connectedStacks = stacksToCheck[i].getMatchingConnections(
          color,
          (s) => s.isBlocked() || checkedSet.has(s) || s.getTopmostColor() !== color,
        );

        connectedStacks.forEach((s) => {
          if (checkedSet.has(s)) return;

          stacksToCheck.push(s);
          checkedSet.add(s);
        });
      }
      result[color] = stacksToCheck.reduce((acc, stack) => acc + stack.getMatchingColorCount(color), 0);
    });

    return result as Record<TileColor, number>;
  }
}
